import React, { ReactNode, ElementType, ComponentPropsWithoutRef } from 'react';
import { classNames } from '@repo/ui/src/utils/classNames';

type MaxWidth =
  | '2xl'
  | '4xl'
  | 'narrow'
  | 'content'
  | 'small-body'
  | 'default'
  | 'body'
  | 'wide'
  | 'extra-wide'
  | 'full';

const maxWidthClasses: Record<MaxWidth, string> = {
  '2xl': 'max-w-2xl',
  '4xl': 'max-w-4xl',
  narrow: 'max-w-3xl',
  content: 'max-w-[1000px]',
  'small-body': 'max-w-smallBody',
  default: 'max-w-7xl',
  body: 'max-w-body',
  wide: 'max-w-[96rem]',
  'extra-wide': 'max-w-[120rem]',
  full: 'max-w-none',
};

type Props<T extends ElementType> = {
  as?: T;
  children: ReactNode;
  className?: string;
  dataTestId?: string;
  maxWidth?: MaxWidth;
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children' | 'className' | 'dataTestId' | 'maxWidth'>;

const Container = <T extends ElementType = 'div'>({
  as,
  children,
  className = '',
  dataTestId,
  maxWidth = 'full',
  ...rest
}: Props<T>) => {
  const Component = as || 'div';

  return (
    <Component
      className={classNames(
        'px-4 sm:px-10 lg:px-20',
        maxWidthClasses[maxWidth],
        maxWidth !== 'full' && 'mx-auto',
        className,
      )}
      data-testid={dataTestId}
      {
        ...(rest as any) /* Cast needed: TS cannot narrow ComponentPropsWithoutRef<T> through generic spread */
      }
    >
      {children}
    </Component>
  );
};

export default Container;
